import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import {
  ASYNC_TASK_STATUS,
  RoutineSuggestionsAsyncErrorCode,
  ROUTINE_SUGGESTIONS
} from 'constants/routine-suggestions-revamp';
import { ActivityType } from 'interfaces/common.interface';

export class RoutineSuggestionsAsyncError extends Error {
  readonly code: RoutineSuggestionsAsyncErrorCode;

  constructor(code: RoutineSuggestionsAsyncErrorCode) {
    super(code);
    this.name = 'RoutineSuggestionsAsyncError';
    this.code = code;
    Object.setPrototypeOf(this, RoutineSuggestionsAsyncError.prototype);
  }
}

type ParsedAsyncTask = {
  kind: ASYNC_TASK_STATUS;
  result?: Record<string, ActivityType[]>;
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const id = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort);
  });

export const extractAsyncTaskId = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const o = data as Record<string, unknown>;
  const camel = o.asyncTaskId;
  const snake = o.async_task_id;
  if (typeof camel === 'string' && camel.length > 0) {
    return camel;
  }
  if (typeof snake === 'string' && snake.length > 0) {
    return snake;
  }
  return undefined;
};

const parseCompletedAsyncTask = (data: unknown): ParsedAsyncTask => {
  if (!data || typeof data !== 'object') {
    return { kind: ASYNC_TASK_STATUS.PENDING };
  }
  const o = data as Record<string, unknown>;
  const status = typeof o.status === 'string' ? o.status : '';

  if (
    status === ASYNC_TASK_STATUS.FAILED ||
    status === ASYNC_TASK_STATUS.ERROR
  ) {
    return { kind: ASYNC_TASK_STATUS.FAILED };
  }

  if (status !== ASYNC_TASK_STATUS.COMPLETED) {
    return { kind: ASYNC_TASK_STATUS.PENDING };
  }

  let rawResult: unknown;
  if (o.metadata && typeof o.metadata === 'object') {
    const meta = o.metadata as Record<string, unknown>;
    rawResult = meta.result;
  }
  if (rawResult == null && o.result != null) {
    rawResult = o.result;
  }

  if (Array.isArray(rawResult)) {
    throw new RoutineSuggestionsAsyncError(
      RoutineSuggestionsAsyncErrorCode.TASK_FAILED
    );
  }

  if (rawResult && typeof rawResult === 'object' && !Array.isArray(rawResult)) {
    return {
      kind: ASYNC_TASK_STATUS.COMPLETED,
      result: rawResult as Record<string, ActivityType[]>
    };
  }

  return { kind: ASYNC_TASK_STATUS.PENDING };
};

export async function pollRoutineSuggestionsResult(
  asyncTaskId: string,
  signal?: AbortSignal
): Promise<Record<string, ActivityType[]>> {
  for (
    let attempt = ROUTINE_SUGGESTIONS.POLL_INITIAL_ATTEMPT;
    attempt < ROUTINE_SUGGESTIONS.POLL_MAX_ATTEMPTS;
    attempt++
  ) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const { data } = await focusBearApi.get(
      Endpoints.ASYNC_TASK_BY_ID(asyncTaskId),
      { signal }
    );
    const parsed = parseCompletedAsyncTask(data);

    if (parsed.kind === ASYNC_TASK_STATUS.FAILED) {
      throw new RoutineSuggestionsAsyncError(
        RoutineSuggestionsAsyncErrorCode.TASK_FAILED
      );
    }
    if (parsed.kind === ASYNC_TASK_STATUS.COMPLETED) {
      return parsed.result ?? {};
    }

    await sleep(ROUTINE_SUGGESTIONS.POLL_MS, signal);
  }

  throw new RoutineSuggestionsAsyncError(
    RoutineSuggestionsAsyncErrorCode.TASK_TIMED_OUT
  );
}
